/**
 * BusinessSettings.jsx
 * Admin-only panel to manage business settings:
 * - Upload / change business logo (from device or URL)
 * - Logo is used in PDF receipts
 */
import React, { useState, useEffect } from "react";

const BASE_URL = process.env.REACT_APP_API_URL || "";

// ─── Compress image for storage ───────────────────────────────────────────────
// Target: under 40KB base64 (~49000 chars) to fit in Google Sheets cell limit
function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        // Step 1: scale to max 200px
        let { width, height } = img;
        const maxW = 200;
        if (width > maxW) { height = Math.round((height * maxW) / width); width = maxW; }
        canvas.width  = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);

        // Step 2: try decreasing quality until it fits
        const qualities = [0.7, 0.5, 0.35, 0.2];
        for (const q of qualities) {
          const result = canvas.toDataURL("image/jpeg", q);
          if (result.length <= 49000) { resolve(result); return; }
        }
        // If still too large, reject with helpful message
        reject(new Error("Image is too large even after compression. Please use an external image URL instead (e.g. upload to imgur.com and paste the link)."));
      };
      img.onerror = () => reject(new Error("Could not read image"));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

export default function BusinessSettings({ business, onLogoUpdated }) {
  const [logo,      setLogo]      = useState(business.logo || "");
  const [urlInput,  setUrlInput]  = useState("");
  const [loading,   setLoading]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");
  const [success,   setSuccess]   = useState("");

  // Fetch latest logo on mount
  useEffect(() => {
    const fetchLogo = async () => {
      setLoading(true);
      try {
        const res  = await fetch(`${BASE_URL}/api/businesses/${business.businessId}/logo`);
        const data = await res.json();
        if (data.success) setLogo(data.logo || "");
      } catch {}
      finally { setLoading(false); }
    };
    fetchLogo();
  }, [business.businessId]);

  // ── Upload from device ──────────────────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError("Image too large. Use an image under 10MB."); return; }
    setError(""); setSuccess("");
    try {
      const compressed = await compressImage(file);
      setLogo(compressed);
      setUrlInput("");
    } catch { setError("Could not process image. Try a JPG or PNG file."); }
  };

  // ── Use URL ─────────────────────────────────────────────────────────────────
  const handleUseUrl = () => {
    if (!urlInput.trim()) return;
    setLogo(urlInput.trim());
    setUrlInput("");
    setError(""); setSuccess("");
  };

  // ── Save to backend ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true); setError(""); setSuccess("");
    try {
      const res  = await fetch(`${BASE_URL}/api/businesses/${business.businessId}/logo`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ logo }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess("Logo saved! It will appear on all new receipts.");
        if (onLogoUpdated) onLogoUpdated(logo);
      } else { setError(data.message || "Failed to save logo."); }
    } catch { setError("Network error. Please try again."); }
    finally { setSaving(false); }
  };

  // ── Remove logo ─────────────────────────────────────────────────────────────
  const handleRemove = () => {
    setLogo(""); setUrlInput(""); setError(""); setSuccess("");
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <h3 style={{ fontSize: 18, marginBottom: 4 }}>🏢 Business Logo</h3>
      <p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 22 }}>
        Your logo appears on PDF receipts generated for guests.
        Recommended: square image, at least 200×200px.
      </p>

      {/* Current logo preview */}
      <div className="card" style={{ padding: "24px", marginBottom: 20 }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary)",
          textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>
          Current Logo
        </p>

        {loading ? (
          <div className="skeleton" style={{ width: 120, height: 120, borderRadius: 16 }} />
        ) : logo ? (
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <img
              src={logo}
              alt="Business logo"
              style={{ width: 120, height: 120, objectFit: "contain",
                borderRadius: 12, border: "1px solid var(--border)",
                background: "transparent", padding: 0 }}
              onError={(e) => { e.target.style.display = "none"; setError("Logo URL is broken — please upload a new one."); }}
            />
            <div>
              <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                {business.name}
              </p>
              <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
                {logo.startsWith("data:") ? "Uploaded from device" : "External URL"}
              </p>
              <button
                className="btn btn-ghost"
                onClick={handleRemove}
                style={{ fontSize: 12, padding: "5px 12px", color: "var(--accent-rose)",
                  border: "1px solid rgba(255,107,107,0.3)" }}
              >
                🗑️ Remove Logo
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 120, height: 120, borderRadius: 16,
              background: "var(--bg-secondary)", border: "2px dashed var(--border)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexDirection: "column", gap: 6, color: "var(--text-muted)" }}>
              <span style={{ fontSize: 32 }}>🏢</span>
              <span style={{ fontSize: 11 }}>No logo</span>
            </div>
            <div>
              <p style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                No logo uploaded yet.
              </p>
              <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                Your business name will be used on receipts.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Upload options */}
      <div className="card" style={{ padding: "24px", marginBottom: 16 }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary)",
          textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16 }}>
          Upload New Logo
        </p>

        {/* Upload from device */}
        <label htmlFor="logo-upload" style={{
          display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
          background: "var(--bg-secondary)", border: "1px dashed var(--border)",
          borderRadius: 10, padding: "14px 16px", marginBottom: 14,
          transition: "border-color 0.2s",
        }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--accent-gold)"}
          onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--border)"}
        >
          <span style={{ fontSize: 24 }}>📷</span>
          <div>
            <p style={{ fontSize: 13, fontWeight: 600 }}>Upload from device</p>
            <p style={{ fontSize: 11, color: "var(--text-muted)" }}>JPG, PNG — auto compressed</p>
          </div>
          <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-muted)" }}>Click to browse</span>
        </label>
        <input id="logo-upload" type="file" accept="image/*"
          style={{ display: "none" }} onChange={handleFileUpload} />

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>or paste image URL</span>
          <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
        </div>

        {/* URL input */}
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://yoursite.com/logo.png"
            style={{ flex: 1, background: "var(--bg-input)", border: "1px solid var(--border)",
              borderRadius: 8, color: "var(--text-primary)", fontSize: 13,
              padding: "9px 12px", outline: "none", fontFamily: "var(--font-body)" }}
            onKeyDown={(e) => e.key === "Enter" && handleUseUrl()}
          />
          <button className="btn btn-ghost" onClick={handleUseUrl}
            style={{ padding: "9px 16px", fontSize: 13, flexShrink: 0 }}>
            Use URL
          </button>
        </div>

        {/* Preview of new upload before saving */}
        {logo && (
          <div style={{ marginTop: 14, padding: "12px 14px", background: "var(--bg-secondary)",
            borderRadius: 8, border: "1px solid var(--border)",
            display: "flex", alignItems: "center", gap: 12 }}>
            <img src={logo} alt="preview" style={{ width: 40, height: 40, objectFit: "contain",
              borderRadius: 6, background: "#fff", padding: 2, border: "1px solid var(--border)" }}
              onError={(e) => e.target.style.display = "none"} />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>
                Preview looks good?
              </p>
              <p style={{ fontSize: 11, color: "var(--text-muted)" }}>
                Click "Save Logo" to apply it to all receipts
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Error / success */}
      {error   && <div style={{ background: "rgba(255,107,107,0.1)", border: "1px solid rgba(255,107,107,0.3)",
        borderRadius: 8, padding: "10px 14px", color: "var(--accent-rose)", fontSize: 13, marginBottom: 12 }}>❌ {error}</div>}
      {success && <div style={{ background: "rgba(81,207,102,0.1)", border: "1px solid rgba(81,207,102,0.3)",
        borderRadius: 8, padding: "10px 14px", color: "var(--accent-green)", fontSize: 13, marginBottom: 12 }}>✅ {success}</div>}

      {/* Save button */}
      <button className="btn btn-primary" onClick={handleSave}
        disabled={saving} style={{ width: "100%", padding: "13px", fontSize: 15 }}>
        {saving ? "Saving…" : "💾 Save Logo"}
      </button>

      <p style={{ fontSize: 11, color: "var(--text-muted)", textAlign: "center", marginTop: 10 }}>
        Logo is stored securely in your Google Sheet
      </p>
    </div>
  );
}
